/**
 * Block type → lazy React component (one dynamic chunk per implementation).
 */
import { lazy } from 'react'
import { BLOCKS_REGISTRY } from './blocks-registry-entries'
import { resolveBlockType } from './blocks-registry-resolve'
import { BLOCK_COMPONENT_LOADERS } from './block-component-loaders'

/** @type {Map<string, React.LazyExoticComponent<React.ComponentType<unknown>>>} */
const lazyComponentCache = new Map()

/**
 * @param {string} type - block_* or legacy type id
 * @returns {React.LazyExoticComponent<React.ComponentType<unknown>>|null}
 */
export function getBlockComponent(type) {
  const resolved = resolveBlockType(type)
  const entry = BLOCKS_REGISTRY[resolved]
  if (!entry) return null

  const componentKey = entry.component
  if (lazyComponentCache.has(componentKey)) {
    return lazyComponentCache.get(componentKey) ?? null
  }

  const loader = BLOCK_COMPONENT_LOADERS[componentKey]
  if (!loader) {
    if (typeof process !== 'undefined') {
      console.warn(
        `[notebook] No loader for component "${componentKey}" (type "${resolved}")`,
      )
    }
    return null
  }

  const LazyComp = lazy(loader)
  lazyComponentCache.set(componentKey, LazyComp)
  return LazyComp
}

export function getResolvedBlockMeta(type) {
  const resolved = resolveBlockType(type)
  return { resolved, entry: BLOCKS_REGISTRY[resolved] ?? null }
}

/** Preload a block chunk (e.g. launcher hover) without mounting. */
export function preloadBlockComponent(type) {
  const resolved = resolveBlockType(type)
  const entry = BLOCKS_REGISTRY[resolved]
  if (!entry) return
  const loader = BLOCK_COMPONENT_LOADERS[entry.component]
  if (loader) void loader()
}
